/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import {
  HotSwapType,
  LiveAudioContext,
} from "audioProcessing/LiveAudioContext";
import { exportToBlob } from "audioProcessing/ExportToBlob";
import AudioBufferCache from "audioProcessing/AudioBufferCache";
import { TARGET_SAMPLE_RATE } from "constants/RecorderSettings";
import { AssetSet } from "types/Asset";
import { AssetId } from "@phonos/types";
import { PlaylistClip } from "types/Clip";

export default class AudioContextManager {
  private _audioBufferCache = new AudioBufferCache();
  private _liveAudioContext = new LiveAudioContext(this._audioBufferCache);
  private _playlist: PlaylistClip[] = [];

  async load(assets: AssetSet): Promise<void> {
    await this._audioBufferCache.loadRemoteAssets(assets);
  }

  free(assetId: AssetId): void {
    this._audioBufferCache.free(assetId);
  }

  async setPlaylist(playlist: PlaylistClip[]): Promise<void> {
    this._playlist = playlist;
    await this._liveAudioContext.setPlaylist(playlist);
  }

  async queuePlaylist(
    seekTime: number,
    onPlaybackComplete: VoidFunction
  ): Promise<void> {
    await this._liveAudioContext.queuePlaylist(seekTime, onPlaybackComplete);
  }

  get playlist(): PlaylistClip[] {
    return this._playlist;
  }

  //Unused but could be useful in the future
  get playbackComplete(): boolean {
    return this._liveAudioContext.playbackComplete;
  }

  get elapsedTime(): number {
    return this._liveAudioContext.elapsedTime;
  }

  get outputLatency(): number {
    return this._liveAudioContext.outputLatency;
  }

  get isPlaying(): boolean {
    return this._liveAudioContext.isPlaying;
  }

  get duration(): number {
    return this._liveAudioContext.duration;
  }

  async play(): Promise<void> {
    await this._liveAudioContext.play();
  }

  async pause(): Promise<void> {
    await this._liveAudioContext.pause();
  }

  async reset(): Promise<void> {
    await this._liveAudioContext.pause();
    this._liveAudioContext.reset();
    this._audioBufferCache.reset();
    this._audioBufferCache = new AudioBufferCache();
    this._liveAudioContext = new LiveAudioContext(this._audioBufferCache);
    this._playlist = [];
  }

  export(outputFilename?: string): Promise<File> {
    return exportToBlob(
      TARGET_SAMPLE_RATE,
      this._playlist,
      this._audioBufferCache,
      outputFilename
    );
  }

  getAssetAudioBuffer(
    assetId: AssetId
  ): Promise<AudioBuffer | null> | undefined {
    return this._audioBufferCache.getAssetAudioBuffer(assetId);
  }

  loadLocalAsset(assetId: AssetId, blob: Blob): Promise<AudioBuffer | null> {
    return this._audioBufferCache.loadLocalAsset(assetId, blob);
  }

  async hotSwapPlaylist(
    newPlayList: PlaylistClip[]
  ): Promise<HotSwapType | null> {
    const hotSwapped =
      await this._liveAudioContext.hotSwapPlaylist(newPlayList);
    if (hotSwapped !== null) {
      this._playlist = newPlayList;
    }
    return hotSwapped;
  }
}

export { HotSwapType } from "audioProcessing/LiveAudioContext";
